import { media } from './media'

type Lang = 'fr' | 'es' | 'en'

export interface ProductOption {
  id: string
  price: number
  label: Record<Lang, string>
  /** Optional photo shown when this option is selected. */
  image?: string
  /** Shopify variant id for this option. */
  variantId?: string
}

/** Prices are language-independent (CAD) and keyed by the product id used in translations. */
export interface ProductPricing {
  id: string
  /** Category id — must match one of PRODUCT_CATEGORIES. */
  category: string
  price: number
  image: string
  /** Shopify variant id — fill these in to send the cart straight to checkout. */
  variantId?: string
  /** Shopify product handle, e.g. "cake-pops" for /products/cake-pops. */
  handle?: string
  unit: Record<Lang, string>
  options?: ProductOption[]
}

export const PRODUCT_CATEGORIES = [
  'all',
  'alfajores',
  'chocolats',
  'tablettes',
  'croustillants',
  'cakepops',
] as const

const boxOptions: ProductOption[] = [
  {
    id: '12',
    price: 26.4,
    label: {
      fr: '12 chocolats',
      es: '12 chocolates',
      en: '12 chocolates',
    },
  },
  {
    id: '24',
    price: 50.4,
    label: {
      fr: '24 chocolats',
      es: '24 chocolates',
      en: '24 chocolates',
    },
  },
  {
    id: '36',
    price: 72,
    label: {
      fr: '36 chocolats',
      es: '36 chocolates',
      en: '36 chocolates',
    },
  },
]

export const productPricing: ProductPricing[] = [
  {
    id: 'alfajores',
    category: 'alfajores',
    price: 16.5,
    image: media.alfajoresConfiture,
    handle: 'alfajores',
    unit: {
      fr: 'la boîte de 12',
      es: 'la caja de 12',
      en: 'box of 12',
    },
    options: [
      {
        id: 'confiture-12',
        price: 16.5,
        image: media.alfajoresConfiture,
        label: {
          fr: 'Dulce de leche & confiture de fraise · 12',
          es: 'Dulce de leche y mermelada de fresa · 12',
          en: 'Dulce de leche & strawberry jam · 12',
        },
      },
      {
        id: 'confiture-24',
        price: 32,
        image: media.alfajoresConfiture,
        label: {
          fr: 'Dulce de leche & confiture de fraise · 24',
          es: 'Dulce de leche y mermelada de fresa · 24',
          en: 'Dulce de leche & strawberry jam · 24',
        },
      },
      {
        id: 'confiture-36',
        price: 48,
        image: media.alfajoresConfiture,
        label: {
          fr: 'Dulce de leche & confiture de fraise · 36',
          es: 'Dulce de leche y mermelada de fresa · 36',
          en: 'Dulce de leche & strawberry jam · 36',
        },
      },
      {
        id: 'confiture-60',
        price: 78,
        image: media.alfajoresConfiture,
        label: {
          fr: 'Dulce de leche & confiture de fraise · 60',
          es: 'Dulce de leche y mermelada de fresa · 60',
          en: 'Dulce de leche & strawberry jam · 60',
        },
      },
    ],
  },
  {
    id: 'alfajores-enrobes',
    category: 'alfajores',
    price: 25.4,
    image: media.alfajoresChocolat,
    handle: 'alfajores-enrobes-du-chocolat',
    unit: {
      fr: 'la boîte de 12',
      es: 'la caja de 12',
      en: 'box of 12',
    },
    options: [
      {
        id: 'chocolat-noir',
        price: 25.4,
        image: media.alfajoresChocolat,
        label: {
          fr: 'Chocolat noir · 12',
          es: 'Chocolate negro · 12',
          en: 'Dark chocolate · 12',
        },
      },
      {
        id: 'chocolat-blanc',
        price: 25.4,
        image: media.alfajoresChocolatBlanc,
        label: {
          fr: 'Chocolat blanc · 12',
          es: 'Chocolate blanco · 12',
          en: 'White chocolate · 12',
        },
      },
      {
        id: 'chocolat-lait',
        price: 25.4,
        image: media.alfajoresChocolatLait,
        label: {
          fr: 'Chocolat au lait · 12',
          es: 'Chocolate con leche · 12',
          en: 'Milk chocolate · 12',
        },
      },
    ],
  },
  {
    id: 'bonbons',
    category: 'chocolats',
    price: 26.4,
    image: media.bonbonsPralines,
    handle: 'chocolats-pralines-fruits',
    unit: {
      fr: 'la boîte de 12',
      es: 'la caja de 12',
      en: 'box of 12',
    },
    options: boxOptions,
  },
  {
    id: 'pralines',
    category: 'chocolats',
    price: 5,
    image: media.pralines,
    handle: 'pralines-maison-aux-noix',
    unit: {
      fr: 'le paquet de 4',
      es: 'el paquete de 4',
      en: 'pack of 4',
    },
  },
  {
    id: 'fruits',
    category: 'chocolats',
    price: 26.4,
    image: media.bonbonsFruitsCoeur,
    handle: 'chocolats-pralines-fruits',
    unit: {
      fr: 'la boîte de 12',
      es: 'la caja de 12',
      en: 'box of 12',
    },
    options: boxOptions,
  },
  {
    id: 'tablettes',
    category: 'tablettes',
    price: 5,
    image: media.tablettesAssorties,
    handle: 'tablettes-de-chocolat',
    unit: {
      fr: 'la tablette',
      es: 'la tableta',
      en: 'per bar',
    },
    options: [
      {
        id: 'blanc',
        price: 5,
        image: media.tabletteBlanche,
        label: {
          fr: 'Chocolat blanc',
          es: 'Chocolate blanco',
          en: 'White chocolate',
        },
      },
      {
        id: 'lait',
        price: 5,
        image: media.tabletteLait,
        label: {
          fr: 'Chocolat au lait',
          es: 'Chocolate con leche',
          en: 'Milk chocolate',
        },
      },
      {
        id: 'noir',
        price: 5,
        image: media.tabletteNoire,
        label: {
          fr: 'Chocolat noir 70 %',
          es: 'Chocolate negro 70 %',
          en: '70% dark chocolate',
        },
      },
    ],
  },
  {
    id: 'pretzels',
    category: 'croustillants',
    price: 13,
    image: media.pretzelsNoir,
    handle: 'pretzels-enrobes-de-chocolat',
    unit: {
      fr: 'la boîte de 10 unités',
      es: 'la caja de 10 unidades',
      en: 'box of 10 pieces',
    },
    options: [
      {
        id: 'lait',
        price: 13,
        image: media.pretzelsBlanc,
        label: {
          fr: '10 pretzels · chocolat au lait',
          es: '10 pretzels · chocolate con leche',
          en: '10 pretzels · milk chocolate',
        },
      },
      {
        id: 'noir',
        price: 13,
        image: media.pretzelsNoir,
        label: {
          fr: '10 pretzels · chocolat noir 70 %',
          es: '10 pretzels · chocolate negro 70 %',
          en: '10 pretzels · 70% dark chocolate',
        },
      },
    ],
  },
  {
    id: 'cakepops',
    category: 'cakepops',
    price: 3,
    image: media.cakepops,
    handle: 'cake-pops',
    unit: {
      fr: 'l’unité',
      es: 'la unidad',
      en: 'per piece',
    },
  },
]

export function getPricing(id: string): ProductPricing | undefined {
  return productPricing.find((pricing) => pricing.id === id)
}

export function getOption(
  id: string,
  optionId?: string,
): ProductOption | undefined {
  if (!optionId) return undefined
  return getPricing(id)?.options?.find((option) => option.id === optionId)
}

/** Resolves the unit price for a product, taking the selected option into account. */
export function resolvePrice(id: string, optionId?: string): number {
  const pricing = getPricing(id)
  if (!pricing) return 0
  return getOption(id, optionId)?.price ?? pricing.price
}

export function formatPrice(value: number): string {
  return Number.isInteger(value) ? `${value} $` : `${value.toFixed(2)} $`
}
